import { createTranslator } from 'next-intl'
import type { Translation } from 'vanilla-cookieconsent'
import * as CookieConsent from 'vanilla-cookieconsent'
import { WEBSITE_LINKS } from '../../../lib/constants'

type JsonObject = Record<string, unknown>

export const CAT_NECESSARY = 'necessary'
export const CAT_ANALYTICS = 'analytics'
export const CAT_ADVERTISEMENT = 'advertisement'
export const CAT_FUNCTIONALITY = 'functionality'
export const CAT_SECURITY = 'security'

export const SERVICE_AD_STORAGE = 'ad_storage'
export const SERVICE_AD_USER_DATA = 'ad_user_data'
export const SERVICE_AD_PERSONALIZATION = 'ad_personalization'
export const SERVICE_ANALYTICS_STORAGE = 'analytics_storage'
export const SERVICE_FUNCTIONALITY_STORAGE = 'functionality_storage'
export const SERVICE_PERSONALIZATION_STORAGE = 'personalization_storage'
export const SERVICE_SECURITY_STORAGE = 'security_storage'

const deepMerge = <T>(base: T, override: Partial<T>): T => {
  if (Array.isArray(base) && Array.isArray(override)) {
    // Replace arrays entirely by override when provided
    return (override as unknown as T) ?? base
  }
  if (
    base &&
    typeof base === 'object' &&
    override &&
    typeof override === 'object'
  ) {
    const result: Record<string, unknown> = {
      ...(base as Record<string, unknown>),
    }
    for (const [key, value] of Object.entries(
      override as Record<string, unknown>
    )) {
      const baseVal = (base as Record<string, unknown>)[key]
      if (
        baseVal &&
        typeof baseVal === 'object' &&
        value &&
        typeof value === 'object' &&
        !Array.isArray(baseVal) &&
        !Array.isArray(value)
      ) {
        result[key] = deepMerge(baseVal, value as Record<string, unknown>)
      } else if (value !== undefined) {
        result[key] = value
      }
    }
    return result as unknown as T
  }
  return (override as unknown as T) ?? base
}

const translateTree = (
  t: (id: string, values?: Record<string, string>) => string,
  namespace: string,
  node: unknown,
  values: Record<string, string>,
  path: Array<string | number> = []
): unknown => {
  if (typeof node === 'string') {
    const hasArrayIndex = path.some((p) => typeof p === 'number')
    if (hasArrayIndex) {
      // Avoid calling t() with numeric path segments (not supported reliably); just interpolate.
      return node.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '')
    }
    const id = [namespace, ...path.map((p) => String(p))].join('.')
    const translated = t(id, values)
    if (translated === id) {
      return node.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '')
    }
    return translated
  }
  if (Array.isArray(node)) {
    return node.map((item, index) =>
      translateTree(t, namespace, item, values, [...path, index])
    )
  }
  if (node && typeof node === 'object') {
    const output: JsonObject = {}
    for (const [key, value] of Object.entries(node as JsonObject)) {
      output[key] = translateTree(t, namespace, value, values, [...path, key])
    }
    return output
  }
  return node
}

const buildPlaceholders = (domain: string) => ({
  privacyPolicy: WEBSITE_LINKS.privacyPolicy,
  helpLink: WEBSITE_LINKS.help,
  domain,

  categoryAnalytics: CAT_ANALYTICS,
  categoryAdvertisement: CAT_ADVERTISEMENT,
  categoryFunctionality: CAT_FUNCTIONALITY,
  categorySecurity: CAT_SECURITY,
  categoryNecessary: CAT_NECESSARY,

  serviceAnalyticsStorage: SERVICE_ANALYTICS_STORAGE,
  serviceAdStorage: SERVICE_AD_STORAGE,
  serviceAdUserData: SERVICE_AD_USER_DATA,
  serviceAdPersonalization: SERVICE_AD_PERSONALIZATION,
  serviceFunctionalityStorage: SERVICE_FUNCTIONALITY_STORAGE,
  servicePersonalizationStorage: SERVICE_PERSONALIZATION_STORAGE,
  serviceSecurityStorage: SERVICE_SECURITY_STORAGE,

  cookieLocale: 'NEXT_LOCALE',
  cookieAuth: process.env.NEXT_PUBLIC_AUTH_COOKIE_NAME || '',
  cookieConsent: 'cc_cookie',

  cookieGa: '_ga',
  cookieGid: '_gid',

  cookieHelpScout: 'HS:Beacon:Suggestions:*',
  cookieHelpScoutId: 'HS:Beacon:Suggestions:id:*',
  cookieHelpScoutBeacon: 'HS:Beacon:*',
  cookieHelpScoutBeaconMessage: 'HS:Beacon:id:*',
  cookieHelpScoutBeaconSuggestions: 'HS:Beacon:Suggestions:*',
  cookieHelpScoutBeaconSuggestionsId: 'HS:Beacon:Suggestions:id:*',
})

const loadTranslation = async (
  lang: 'en' | 'es' | 'pt' | 'fr' | 'it' | 'de',
  domain: string
): Promise<Translation> => {
  const placeholders = buildPlaceholders(domain)
  const loadWithMessages = (
    messages: Translation,
    locale: string
  ): Translation => {
    const createAny = createTranslator as unknown as (options: any) => any
    const t = createAny({
      locale,
      messages: { cookieConsent: messages as unknown as JsonObject },
      onError: () => {},
    }) as unknown as (id: string, values?: Record<string, string>) => string
    return translateTree(
      t,
      'cookieConsent',
      messages,
      placeholders
    ) as Translation
  }

  // Always load English as base for per-key fallback
  const enMod = await import('@/locales/en/cookie-consent.json')
  const enMessages = enMod.default as Translation

  if (lang === 'en') {
    return loadWithMessages(enMessages, 'en')
  }

  try {
    const localeMod = await import(`@/locales/${lang}/cookie-consent.json`)
    const merged = deepMerge<Translation>(
      enMessages,
      localeMod.default as Translation
    )
    return loadWithMessages(merged, lang)
  } catch {
    return loadWithMessages(enMessages, 'en')
  }
}

export function initializeDataLayerWithDefaultConsent() {
  if (typeof window === 'undefined') {
    return
  }

  window.dataLayer = window.dataLayer || []

  function gtag() {
    window.dataLayer.push(arguments)
  }

  window.gtag = window.gtag || (gtag as Gtag.Gtag)

  // Set default consent to 'denied' (this should happen before changing any other dataLayer)
  window.gtag('consent', 'default', {
    [SERVICE_AD_STORAGE]: 'denied',
    [SERVICE_AD_USER_DATA]: 'denied',
    [SERVICE_AD_PERSONALIZATION]: 'denied',
    [SERVICE_ANALYTICS_STORAGE]: 'denied',
    [SERVICE_FUNCTIONALITY_STORAGE]: 'denied',
    [SERVICE_PERSONALIZATION_STORAGE]: 'denied',
    [SERVICE_SECURITY_STORAGE]: 'denied',
  })

  return () => {
    delete window.gtag
    delete window.dataLayer
  }
}

function updateGtagConsent() {
  window.gtag('consent', 'update', {
    [SERVICE_ANALYTICS_STORAGE]: CookieConsent.acceptedService(
      SERVICE_ANALYTICS_STORAGE,
      CAT_ANALYTICS
    )
      ? 'granted'
      : 'denied',
    [SERVICE_AD_STORAGE]: CookieConsent.acceptedService(
      SERVICE_AD_STORAGE,
      CAT_ADVERTISEMENT
    )
      ? 'granted'
      : 'denied',
    [SERVICE_AD_USER_DATA]: CookieConsent.acceptedService(
      SERVICE_AD_USER_DATA,
      CAT_ADVERTISEMENT
    )
      ? 'granted'
      : 'denied',
    [SERVICE_AD_PERSONALIZATION]: CookieConsent.acceptedService(
      SERVICE_AD_PERSONALIZATION,
      CAT_ADVERTISEMENT
    )
      ? 'granted'
      : 'denied',
    [SERVICE_FUNCTIONALITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_FUNCTIONALITY_STORAGE,
      CAT_FUNCTIONALITY
    )
      ? 'granted'
      : 'denied',
    [SERVICE_PERSONALIZATION_STORAGE]: CookieConsent.acceptedService(
      SERVICE_PERSONALIZATION_STORAGE,
      CAT_FUNCTIONALITY
    )
      ? 'granted'
      : 'denied',
    [SERVICE_SECURITY_STORAGE]: CookieConsent.acceptedService(
      SERVICE_SECURITY_STORAGE,
      CAT_SECURITY
    )
      ? 'granted'
      : 'denied',
  })
}

/**
 * All config. options available here:
 * https://cookieconsent.orestbida.com/reference/configuration-reference.html
 */
export const getCookieConsentConfig = (
  domain: string,
  defaultLang?: 'en' | 'es' | 'pt' | 'fr' | 'it' | 'de'
): CookieConsent.CookieConsentConfig => ({
  root: '#__next',
  mode: 'opt-in',
  autoShow: true,
  revision: 0,
  manageScriptTags: true,
  autoClearCookies: true,
  hideFromBots: true,
  disablePageInteraction: false,
  lazyHtmlGeneration: true,

  guiOptions: {
    consentModal: {
      layout: 'cloud inline',
      position: 'bottom center',
      equalWeightButtons: true,
      flipButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      equalWeightButtons: true,
      flipButtons: false,
    },
  },

  onFirstConsent: () => {
    updateGtagConsent()
  },
  onConsent: () => {
    updateGtagConsent()
  },
  onChange: () => {
    updateGtagConsent()
  },

  categories: {
    [CAT_NECESSARY]: {
      enabled: true,
      readOnly: true,
    },
    [CAT_ANALYTICS]: {
      autoClear: {
        cookies: [
          {
            name: /^(_ga|_gid)/,
          },
          {
            name: /^_gat/,
          },
          {
            name: /^AMP_TOKEN/,
          },
          {
            name: /^gac/,
          },
        ],
      },
      // See: https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
      services: {
        [SERVICE_ANALYTICS_STORAGE]: {
          label:
            'Enables storage (such as cookies) related to analytics e.g. visit duration.',
        },
      },
    },
    [CAT_ADVERTISEMENT]: {
      services: {
        [SERVICE_AD_STORAGE]: {
          label: 'Enables storage (such as cookies) related to advertising.',
        },
        [SERVICE_AD_USER_DATA]: {
          label:
            'Sets consent for sending user data related to advertising to Google.',
        },
        [SERVICE_AD_PERSONALIZATION]: {
          label: 'Sets consent for personalized advertising.',
        },
      },
    },
    [CAT_FUNCTIONALITY]: {
      services: {
        [SERVICE_FUNCTIONALITY_STORAGE]: {
          label:
            'Enables storage that supports the functionality of the website or app e.g. language settings.',
        },
        [SERVICE_PERSONALIZATION_STORAGE]: {
          label:
            'Enables storage related to personalization e.g. video recommendations.',
        },
      },
    },
    [CAT_SECURITY]: {
      services: {
        [SERVICE_SECURITY_STORAGE]: {
          label:
            'Enables storage related to security such as authentication functionality, fraud prevention, and other user protection.',
        },
      },
    },
  },
  language: {
    default: defaultLang || 'en',
    translations: {
      en: async (): Promise<Translation> => loadTranslation('en', domain),
      es: async (): Promise<Translation> => loadTranslation('es', domain),
      pt: async (): Promise<Translation> => loadTranslation('pt', domain),
      fr: async (): Promise<Translation> => loadTranslation('fr', domain),
      it: async (): Promise<Translation> => loadTranslation('it', domain),
      de: async (): Promise<Translation> => loadTranslation('de', domain),
    },
  },
})
